/**
 * Trace driven animations — which loaded AnimationGroups are DRIVEN
 * animations, and by what.
 *
 * A glTF animation is driven when it carries
 * `extras.trace.driver: { node, target }`: its time is the weight of morph
 * target `target` of the node named `node` in the same file (RECORDINGS_SPEC
 * §2.6). The glTF loader hook (`loaders/gltf/TRACE_animation_binding.ts`)
 * stamps the driver onto the group Babylon made for it, together with the
 * clip's glTF input range. The
 * stamp lives on `AnimationGroup.metadata`, which `AnimationGroup.clone`
 * carries (by reference) — so every placement instantiated from the
 * container cache knows its driven groups the same way an inline load does.
 *
 * `SceneObject.setAnimationClips` keeps driven groups out of the clip list
 * (a driven animation is never played by time), and the binder evaluates
 * them from their driver every frame (`loaders/animationBinding/drivenAnimations.ts`).
 */

import type { AnimationGroup } from "@babylonjs/core/Animations/animationGroup";

/** What the loader stamps on a driven group. */
export interface AnimationDriverStamp {
    /** Name of the node whose mesh carries the driving morph target (e.g. `face`). */
    node: string;
    /** Name of the driving morph target (e.g. `jawOpen`). */
    target: string;
    /** Index of the animation in its file. */
    index: number;
    /** min / max of the clip's sampler inputs (seconds): the driven time is clamped to them. */
    inputMin: number;
    inputMax: number;
}

const KEY = "traceDriver";

/** The driver of a loaded group, or undefined for an ordinary (time-played) clip. */
export function getAnimationDriver(group: AnimationGroup): AnimationDriverStamp | undefined {
    const metadata = group.metadata as Record<string, unknown> | null | undefined;
    const stamp = metadata?.[KEY] as { driver?: AnimationDriverStamp } | undefined;
    return stamp?.driver;
}

/** Stamp a group as driven. @internal (the loader hook) */
export function setAnimationDriver(group: AnimationGroup, driver: AnimationDriverStamp): void {
    const metadata = (group.metadata && typeof group.metadata === "object" ? group.metadata : {}) as Record<string, unknown>;
    group.metadata = { ...metadata, [KEY]: { driver } };
}
